import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Table, Field, Input, Select, Pill } from "../hr/ui";
import { Sev, Spinner, Tabs } from "./mui";
import { Modal } from "./TasksChecklists";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const empty = { type: "customer", location_id: "", category: "Service", priority: "medium", description: "", customer_id: "" };

export default function Complaints() {
  const { locationId, locations } = useOutletContext();
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const [resolve, setResolve] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    api.get("/management/complaints", { params: {
      location_id: locationId || undefined, type: type || undefined, status: status || undefined } })
      .then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, [locationId, type, status]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.description.trim()) return toast.error("A description is required");
    try {
      await api.post("/management/complaints", { ...form, location_id: form.location_id || null, customer_id: form.customer_id || null });
      toast.success("Complaint logged"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doResolve = async () => {
    if (!notes.trim()) return toast.error("Resolution notes are required");
    try {
      await api.put(`/management/complaints/${resolve.id}`, { status: "resolved", resolution_notes: notes });
      toast.success("Complaint resolved"); setResolve(null); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!rows) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="mgmt-complaints-page">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs testid="complaint-type-tabs" value={type} onChange={setType}
          tabs={[["", "All"], ["customer", "Customer"], ["staff", "Staff"]]} />
        <div className="flex-1" />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40" data-testid="complaint-status-filter">
          <option value="">All statuses</option>
          {["open", "in_progress", "resolved", "closed"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </Select>
        <Btn variant="primary" onClick={() => setForm({ ...empty })} data-testid="new-complaint-btn"><Plus size={15} /> Log Complaint</Btn>
      </div>

      <Card testid="complaints-card">
        <SectionHead title="Complaints" sub={`${rows.length} record(s) · resolution notes required to close`} />
        <Table testid="complaints-table" columns={[
          { key: "reference", label: "Ref" },
          { key: "type", label: "Type" },
          { key: "category", label: "Category" },
          { key: "location_name", label: "Location" },
          { key: "description", label: "Description" },
          { key: "priority", label: "Priority", render: (r) => <Sev value={r.priority} /> },
          { key: "created_at", label: "Logged", render: (r) => fmtDateTime(r.created_at) },
          { key: "status", label: "Status", render: (r) => <Pill status={r.status === "open" ? "pending" : r.status === "resolved" || r.status === "closed" ? "completed" : "in_progress"} text={r.status} /> },
          { key: "act", label: "", align: "right", render: (r) => !["resolved", "closed"].includes(r.status)
            ? <Btn variant="primary" onClick={() => setResolve(r)} data-testid={`complaint-resolve-${r.id}`}>Resolve</Btn>
            : <span className="text-[11px]" style={{ color: HC.soft }}>{r.resolution_notes}</span> },
        ]} rows={rows} empty="No complaints logged" />
      </Card>

      {form && (
        <Modal title="Log a complaint" onClose={() => setForm(null)} onSave={save} testid="complaint-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="complaint-type-select">
                <option value="customer">Customer</option>
                <option value="staff">Staff</option>
              </Select>
            </Field>
            <Field label="Location">
              <Select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} data-testid="complaint-location-select">
                <option value="">Select location</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </Field>
            <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="Description">
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="complaint-description-input" />
          </Field>
        </Modal>
      )}

      {resolve && (
        <Modal title={`Resolve ${resolve.reference}`} onClose={() => { setResolve(null); setNotes(""); }}
          onSave={doResolve} saveLabel="Resolve" testid="complaint-resolve-modal">
          <Field label="Resolution notes (required)">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="complaint-notes-input" />
          </Field>
        </Modal>
      )}
    </div>
  );
}
